import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO head component.
 * Wrap any page in <SEO ... /> and it will set page-specific meta + JSON-LD.
 *
 * Usage:
 *   <SEO
 *     title="..."
 *     description="..."
 *     path="/freelancers/username"
 *     image="https://..."
 *     jsonLd={{ "@context": "...", "@type": "Person", ... }}
 *   />
 */
const SEO = ({
  title,
  description,
  path = "",
  image = "https://lancercity.com/og-ar.png",
  type = "website",
  lang = "ar",
  jsonLd = null,
  noindex = false,
}) => {
  const url = `https://lancercity.com${path}`;
  const fullTitle = title ? `${title} — لانسر سيتي` : "لانسر سيتي — العمل الحر في العالم العربي";
  const desc = description || "منصة العمل الحر الأولى في العالم العربي — تواصل مع أفضل المستقلين العرب واحصل على خدمات احترافية.";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      {/* hreflang for current path */}
      <link rel="alternate" hrefLang="ar" href={url} />
      <link rel="alternate" hrefLang="ar-SA" href={url} />
      <link rel="alternate" hrefLang="ar-AE" href={url} />
      <link rel="alternate" hrefLang="ar-EG" href={url} />
      <link rel="alternate" hrefLang="en" href={`${url}${url.includes("?") ? "&" : "?"}lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === "ar" ? "ar_SA" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* HTML lang + dir */}
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />

      {/* Per-page JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
